import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle,
  Divider, IconButton, MenuItem, Paper, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import {
  MdAdd, MdClose, MdDelete, MdEdit, MdPlayCircle, MdWarning,
} from "react-icons/md";
import { db } from "../../firebase/firebase-config";
import { uploadTrading, editTraining } from "../store/actions/items";
import Colors from "../../assets/styles/Colors";
import Swal from "sweetalert2";

const CATEGORY_LABELS = {
  mastermind: "NUMIND",
  trading:    "NUINCOME",
  nuwealth:   "NUWEALTH",
  nubusiness: "NUBUSINESS",
};
const CATEGORIES = ["mastermind", "trading", "nuwealth", "nubusiness"];

const PLAN_ACCESS = {
  public: { label: "Público",       bg: Colors.bgElevated, color: Colors.textMuted },
  access: { label: "NuCore Access", bg: `${Colors.primary}22`, color: Colors.primary },
  pro:    { label: "NuCore Pro",    bg: "#082f49", color: "#38bdf8" },
  elite:  { label: "NuCore Elite",  bg: "#2a1200", color: "#fb923c" },
};

function normalizeAccessKey(value) {
  const key = String(value || "").toLowerCase();
  if (key === "public" || key === "all") return "public";
  if (key === "members" || key === "member" || key === "access") return "access";
  if (key === "gold" || key === "goldplan" || key === "elite") return "elite";
  if (key === "pro") return "pro";
  return "";
}

function accessKeyFor(item) {
  return normalizeAccessKey(item.minimumPlanKey || item.requiredPlan)
    || (item.isGoldPlan ? "elite" : item.isMembersOnly ? "access" : "public");
}

function accessFieldsFor(key) {
  const normalized = normalizeAccessKey(key) || "public";
  return {
    minimumPlanKey: normalized,
    requiredPlan: normalized,
    isMembersOnly: normalized !== "public",
    isGoldPlan: normalized === "elite",
  };
}

// ── Add / Edit dialog ─────────────────────────────────────────────────────────

function CourseDialog({ open, onClose, onSaved, section, editData }) {
  const isEdit = !!editData;
  const [name,        setName]        = useState("");
  const [description, setDescription] = useState("");
  const [videoURL,    setVideoURL]    = useState("");
  const [streamURL,   setStreamURL]   = useState("");
  const [orderNumber, setOrderNumber] = useState(0);
  const [group,       setGroup]       = useState("");
  const [membership,  setMembership]  = useState("members");
  const [saving,      setSaving]      = useState(false);

  useEffect(() => {
    if (!open) return;
    if (isEdit && editData) {
      setName(editData.name || "");
      setDescription(editData.description || "");
      setVideoURL((editData.videoURL || "").replace(/&$/, ""));
      setStreamURL(editData.streamURL || "");
      setOrderNumber(editData.orderNumber ?? 0);
      setGroup(editData.section || editData.level || "");
      setMembership(accessKeyFor(editData));
    } else {
      setName(""); setDescription(""); setVideoURL(""); setStreamURL("");
      setOrderNumber(0); setGroup(""); setMembership("members");
    }
  }, [open, isEdit, editData]);

  async function handleSave() {
    if (!name.trim()) { alert("El nombre es requerido."); return; }
    setSaving(true);
    try {
      const trimmedURL = videoURL.trim();
      const data = {
        name:         name.trim(),
        description:  description.trim(),
        videoURL:     trimmedURL.includes("youtube") ? trimmedURL + "&" : trimmedURL,
        streamURL:    streamURL.trim(),
        orderNumber:  Number(orderNumber) || 0,
        section:      group.trim(),
        ...accessFieldsFor(membership),
        cdate:        new Date(),
      };
      if (isEdit) {
        await editTraining(data, section.firestoreTag, editData.id);
      } else {
        await uploadTrading(data, section.firestoreTag);
      }
      onSaved();
      onClose();
    } catch (e) { alert("Error: " + e.message); }
    finally { setSaving(false); }
  }

  const fieldSx = {
    "& .MuiInputBase-input": { color: Colors.textPrimary },
    "& .MuiInputLabel-root": { color: Colors.textSecondary },
    "& .MuiOutlinedInput-root fieldset": { borderColor: Colors.borderPrimary },
    "& .MuiOutlinedInput-root:hover fieldset": { borderColor: Colors.primary + "66" },
    "& .MuiSelect-icon": { color: Colors.textSecondary },
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { backgroundImage: "none", background: Colors.bgSecondary, border: `1px solid ${Colors.borderPrimary}` } }}>
      <DialogTitle sx={{ color: Colors.textPrimary, borderBottom: `1px solid ${Colors.borderSecondary}`, py: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 16 }}>
            {isEdit ? `Editar · ${editData?.name}` : `Agregar curso · ${section?.displayName}`}
          </Typography>
          <IconButton size="small" onClick={onClose} disabled={saving} sx={{ color: Colors.textMuted }}>
            <MdClose />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ pt: "20px !important" }}>
        <Stack spacing={2}>
          <TextField label="Nombre *" value={name} onChange={e => setName(e.target.value)} fullWidth size="small" sx={fieldSx} />
          <TextField label="Descripción" value={description} onChange={e => setDescription(e.target.value)} fullWidth size="small" multiline rows={2} sx={fieldSx} />
          <TextField label="URL de Video (YouTube / Vimeo / MP4)" value={videoURL} onChange={e => setVideoURL(e.target.value)} fullWidth size="small" sx={fieldSx} />
          <TextField label="URL de Transmisión en vivo" value={streamURL} onChange={e => setStreamURL(e.target.value)} fullWidth size="small" sx={fieldSx} />
          <TextField label="Número de orden" type="number" value={orderNumber} onChange={e => setOrderNumber(e.target.value)} fullWidth size="small" sx={fieldSx} />
          <TextField
            label="Grupo / Nivel (ej: Básico, Pro Academy, Power Lab…)"
            value={group} onChange={e => setGroup(e.target.value)}
            fullWidth size="small" placeholder="Básico" sx={fieldSx}
          />
          <TextField select label="Membresía" value={membership} onChange={e => setMembership(e.target.value)} fullWidth size="small" sx={fieldSx}>
            <MenuItem value="public">Público</MenuItem>
            <MenuItem value="access">NuCore Access</MenuItem>
            <MenuItem value="pro">NuCore Pro</MenuItem>
            <MenuItem value="elite">NuCore Elite</MenuItem>
          </TextField>
          <Button variant="contained" onClick={handleSave} disabled={saving} fullWidth
            sx={{ textTransform: "none", borderRadius: 2, py: 1.2, fontWeight: 700, background: Colors.primary, "&:hover": { background: Colors.primaryDark } }}>
            {saving
              ? <CircularProgress size={18} sx={{ color: "#fff" }} />
              : isEdit ? "Guardar cambios" : "Agregar curso"
            }
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

// ── Main view ─────────────────────────────────────────────────────────────────

export default function ContenidoView() {
  const [sections,       setSections]       = useState([]);
  const [loadingSecs,    setLoadingSecs]    = useState(true);
  const [selectedSec,    setSelectedSec]    = useState(null);
  const [courses,        setCourses]        = useState(null);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [dialogOpen,     setDialogOpen]     = useState(false);
  const [editData,       setEditData]       = useState(null);

  // Load sections once on mount
  useEffect(() => {
    db.collection("sections").get()
      .then(snap => {
        const secs = snap.docs
          .filter(d => d.id !== "_hero")
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
        setSections(secs);
      })
      .catch(() => {})
      .finally(() => setLoadingSecs(false));
  }, []);

  // Load courses when selected section changes
  const loadCourses = useCallback((sec) => {
    if (!sec?.firestoreTag) { setCourses(null); return; }
    setLoadingCourses(true);
    setCourses(null);
    db.collection("academy_content")
      .where("tags", "array-contains", sec.firestoreTag)
      .get()
      .then(snap => {
        const docs = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.orderNumber ?? 999) - (b.orderNumber ?? 999));
        setCourses(docs);
      })
      .catch(() => setCourses([]))
      .finally(() => setLoadingCourses(false));
  }, []);

  useEffect(() => { loadCourses(selectedSec); }, [selectedSec, loadCourses]);

  async function handleDelete(course) {
    const result = await Swal.fire({
      title: "¿Eliminar curso?",
      text: `"${course.name}" será eliminado permanentemente.`,
      icon: "warning",
      confirmButtonText: "Eliminar",
      confirmButtonColor: Colors.error,
      showDenyButton: true,
      denyButtonText: "Cancelar",
    });
    if (result.isConfirmed) {
      await db.collection("academy_content").doc(course.id).delete();
      loadCourses(selectedSec);
    }
  }

  function openAdd()        { setEditData(null);   setDialogOpen(true); }
  function openEdit(course) { setEditData(course); setDialogOpen(true); }

  const grouped = CATEGORIES.map(cat => ({ cat, secs: sections.filter(s => s.category === cat) }));

  // ── Sidebar ─────────────────────────────────────────────────────────────────
  const sidebar = (
    <Box sx={{
      width: 256, flexShrink: 0, overflowY: "auto",
      borderRight: `1px solid ${Colors.borderSecondary}`,
      background: Colors.bgSecondary,
    }}>
      <Box sx={{ px: 2, py: 2, borderBottom: `1px solid ${Colors.borderSecondary}` }}>
        <Typography variant="caption" sx={{ color: Colors.textMuted, letterSpacing: 2, fontWeight: 700 }}>SECCIONES</Typography>
      </Box>
      {loadingSecs ? (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}><CircularProgress size={22} /></Box>
      ) : grouped.map(({ cat, secs }) => (
        <Box key={cat}>
          <Box sx={{ px: 2, py: 1, background: Colors.bgTertiary }}>
            <Typography variant="caption" sx={{ color: Colors.textMuted, letterSpacing: 1.5, fontWeight: 700, fontSize: 10 }}>
              {CATEGORY_LABELS[cat]}
            </Typography>
          </Box>
          {secs.length === 0 ? (
            <Box sx={{ px: 2, py: 0.75 }}>
              <Typography variant="caption" sx={{ color: Colors.textMuted, fontSize: 11 }}>Sin secciones</Typography>
            </Box>
          ) : secs.map(sec => {
            const active = selectedSec?.id === sec.id;
            return (
              <Box key={sec.id} onClick={() => setSelectedSec(sec)}
                sx={{
                  px: 2, py: 1.1, cursor: "pointer", display: "flex", alignItems: "center", gap: 1.5,
                  borderLeft: `3px solid ${active ? Colors.primary : "transparent"}`,
                  background: active ? `${Colors.primary}18` : "transparent",
                  "&:hover": { background: active ? `${Colors.primary}18` : Colors.bgTertiary },
                  transition: "background 0.12s",
                }}>
                {sec.icon && <span style={{ fontSize: 15, lineHeight: 1 }}>{sec.icon}</span>}
                <Typography variant="body2" sx={{ color: active ? Colors.textPrimary : Colors.textSecondary, fontSize: 13, fontWeight: active ? 600 : 400 }}>
                  {sec.displayName}
                </Typography>
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );

  // ── Course table ─────────────────────────────────────────────────────────────
  const cellSx = { borderColor: Colors.borderSecondary, py: 1, px: 1.5 };

  const coursePanel = !selectedSec ? (
    <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Typography sx={{ color: Colors.textMuted }}>← Selecciona una sección para ver sus cursos</Typography>
    </Box>
  ) : (
    <Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>
      {/* Header */}
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h6" sx={{ color: Colors.textPrimary, fontWeight: 700 }}>
            {selectedSec.icon && <span style={{ marginRight: 8 }}>{selectedSec.icon}</span>}
            {selectedSec.displayName}
          </Typography>
          <Typography variant="caption" sx={{ color: Colors.textMuted, fontFamily: "monospace" }}>
            {selectedSec.firestoreTag || "sin tag"}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<MdAdd />} onClick={openAdd}
          sx={{ textTransform: "none", borderRadius: 2, background: Colors.primary, "&:hover": { background: Colors.primaryDark }, flexShrink: 0 }}>
          Agregar curso
        </Button>
      </Stack>

      {/* Warning if no firestoreTag */}
      {!selectedSec.firestoreTag && (
        <Paper sx={{ p: 2, mb: 2, display: "flex", gap: 1.5, alignItems: "center", background: `${Colors.warning}18`, border: `1px solid ${Colors.warning}60`, borderRadius: 2 }}>
          <MdWarning size={20} color={Colors.warning} />
          <Typography variant="body2" sx={{ color: Colors.warning }}>
            Esta sección no tiene etiqueta Firestore. Configúrala en Academia → editar sección para poder agregar cursos.
          </Typography>
        </Paper>
      )}

      {loadingCourses || courses === null ? (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 8 }}><CircularProgress /></Box>
      ) : courses.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: "center", border: `1px dashed ${Colors.borderPrimary}`, background: "transparent", borderRadius: 3 }}>
          <Typography sx={{ color: Colors.textMuted, mb: 2 }}>No hay cursos en esta sección todavía.</Typography>
          <Button variant="outlined" onClick={openAdd} sx={{ textTransform: "none", borderColor: Colors.borderPrimary, color: Colors.textSecondary }}>
            + Agregar primer curso
          </Button>
        </Paper>
      ) : (
        <Paper elevation={0} sx={{ border: `1px solid ${Colors.borderSecondary}`, borderRadius: 2, overflow: "hidden" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: Colors.tableHeader }}>
                {["#", "Nombre", "Descripción", "Grupo / Nivel", "Video", "Acceso", "", ""].map((h, i) => (
                  <TableCell key={i} sx={{ ...cellSx, color: Colors.textMuted, fontSize: 10, letterSpacing: 1.2, fontWeight: 700, textTransform: "uppercase" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {courses.map(course => (
                <TableRow key={course.id} sx={{ "&:hover": { background: Colors.tableHover }, "& td": { ...cellSx } }}>
                  <TableCell sx={{ color: Colors.textMuted, width: 36, fontSize: 12 }}>{course.orderNumber ?? "—"}</TableCell>

                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography noWrap sx={{ color: Colors.textPrimary, fontWeight: 500, fontSize: 13 }}>{course.name}</Typography>
                  </TableCell>

                  <TableCell sx={{ maxWidth: 220 }}>
                    <Typography noWrap sx={{ color: Colors.textSecondary, fontSize: 12 }}>{course.description || "—"}</Typography>
                  </TableCell>

                  <TableCell>
                    {(course.section || course.level) ? (
                      <Chip label={course.section || course.level} size="small"
                        sx={{ fontSize: 10, height: 20, background: Colors.bgElevated, color: Colors.textSecondary, borderRadius: 1 }} />
                    ) : <span style={{ color: Colors.textMuted, fontSize: 12 }}>—</span>}
                  </TableCell>

                  <TableCell width={48}>
                    {(course.videoURL || course.streamURL)
                      ? <MdPlayCircle size={18} color={Colors.primary} />
                      : <span style={{ color: Colors.textMuted, fontSize: 12 }}>—</span>
                    }
                  </TableCell>

                  <TableCell>
                    <Chip size="small"
                      label={(PLAN_ACCESS[accessKeyFor(course)] || PLAN_ACCESS.public).label}
                      sx={{
                        fontSize: 10, height: 20, borderRadius: 1,
                        background: (PLAN_ACCESS[accessKeyFor(course)] || PLAN_ACCESS.public).bg,
                        color: (PLAN_ACCESS[accessKeyFor(course)] || PLAN_ACCESS.public).color,
                      }}
                    />
                  </TableCell>

                  <TableCell width={36} sx={{ p: "0 !important" }}>
                    <IconButton size="small" onClick={() => openEdit(course)}
                      sx={{ color: Colors.textSecondary, "&:hover": { color: Colors.primary } }}>
                      <MdEdit size={15} />
                    </IconButton>
                  </TableCell>

                  <TableCell width={36} sx={{ p: "0 !important" }}>
                    <IconButton size="small" onClick={() => handleDelete(course)}
                      sx={{ color: Colors.textMuted, "&:hover": { color: Colors.error } }}>
                      <MdDelete size={15} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: "flex", height: "calc(100vh - 72px)", overflow: "hidden", background: Colors.bgPrimary }}>
      {sidebar}
      {coursePanel}
      <CourseDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={() => loadCourses(selectedSec)}
        section={selectedSec}
        editData={editData}
      />
    </Box>
  );
}
